<template>
  <main>
    <article class="row" v-if="votes.length && portfolioId && edition">
      <section class="col s3" v-for="(vote, idx) in votes.slice(0, 4)" :key="vote.id">
        <div class="card">
          <section class="card-image">
            <button class="btn-floating halfway-fab modal-trigger waves-effect waves-light amber accent-4"
                    data-target="bbn-edit-modal"
                    @click="editModal(idx)"
            >
              <i class="fas fa-pen-alt" />
            </button>
          </section>
          <section class="card-content">
            <h6 class="blue-grey-text text-darken-4">
              {{ vote.instrument.company.instruments }}
              <span class="strong amber-text text-accent-4">{{ vote.instrument.company.name | pretty }}</span>
            </h6>
            <p class="strong">Points: {{ vote.points }}</p>
            <p>{{ vote.comment }}</p>
          </section>
        </div>
      </section>
    </article>
    <article class="row" v-if="lastVote && portfolioId && edition">
      <section class="col s3">
        <div class="card">
          <section class="card-image">
            <button class="btn-floating halfway-fab modal-trigger waves-effect waves-light amber accent-4"
                    data-target="bbn-edit-modal"
                    @click="editModal(4)"
            >
              <i class="fas fa-pen-alt" />
            </button>
          </section>
          <section class="card-content">
            <h6 class="blue-grey-text text-darken-4">
              {{ lastVote.instrument.company.instruments }}
              <span class="strong amber-text text-accent-4">{{ lastVote.instrument.company.name | pretty }}</span>
            </h6>
            <p class="strong">Points: {{ lastVote.points }}</p>
            <p>{{ lastVote.comment }}</p>
          </section>
        </div>
      </section>
      <section class="col s3" />
      <section class="col s3" />
      <section class="col s3" />
    </article>
    <form class="inner-form container" v-if="portfolioId && votes.length < 5">
      <article class="row">
        <section class="col s12">
          <h4 class="left green-text">Add Vote</h4>
        </section>
      </article>
      <article class="row">
        <autocomplete id="bbn-instrument-search"
                      name="Search for Instrument"
                      size="s9"
                      watermark="Ticker / Name"
                      :picks="instrumentLabels"
                      @picked="handleInstrumentSelection"
                      v-if="instrumentLabels.length"
        />
        <section class="input-field col s3">
          <input id="bbn-points-input" type="number" name="Points" v-model="payload.points" max="6" min="1" class="validate">
          <label class="active" for="bbn-points-input">Points</label>
        </section>
      </article>
      <article class="row">
        <section class="input-field col s12">
          <textarea id="bbn-comment-input" class="materialize-textarea" v-model="payload.comment" />
          <label class="active" for="bbn-comment-input">Comment</label>
        </section>
      </article>
      <article class="row">
        <section class="col s9" />
        <section class="col s3">
          <input class="btn green waves-effect waves-light right"
                 type="submit"
                 value="Submit"
                 @click.prevent="saveVote"
          >
        </section>
      </article>
    </form>
    <h5 class="strong text-accent-4"
        :class="{'pink-text': tally === 0, 'green-text': tally > 0}"
        style="position: fixed; bottom: 15px; right: 15px;"
        v-show="portfolioId && edition"
    >
      Points Left: {{ tally }}
    </h5>
    <h5 class="pink-text text-accent-4" v-if="votes.length === 5 && tally">
      <strong>Please allocate more points per vote</strong>
    </h5>
    <aside id="bbn-edit-modal" class="modal">
      <article class="modal-content">
        <section class="row">
          <div class="col s12">
            <h4>Edit Vote</h4>
          </div>
        </section>
        <section class="row">
          <autocomplete id="bbn-edit-modal-instrument-search"
                        name="Search for Instrument"
                        size="s9"
                        watermark="Ticker / Name"
                        :picks="instrumentLabels"
                        @picked="handleInstrumentSelection"
                        v-if="instrumentLabels.length"
          />
          <div class="input-field col s9" v-else />
          <div class="input-field col s3">
            <input id="bbn-edit-modal-points-input" type="number" name="Points" v-model="payload.points" max="6" min="1" class="validate">
            <label class="active" for="bbn-edit-modal-points-input">Points</label>
          </div>
        </section>
        <section class="row">
          <div class="input-field col s12">
            <textarea id="bbn-edit-modal-comment-input" class="materialize-textarea" v-model="payload.comment" />
            <label class="active" for="bbn-edit-modal-comment-input">Comment</label>
          </div>
        </section>
      </article>
      <article class="modal-footer">
        <a class="modal-close waves-effect grey lighten-2 waves-green btn-flat left">Cancel</a>
        <a class="modal-close waves-effect green lighten-3 waves-green btn-flat" @click="saveVote">Save</a>
      </article>
    </aside>
  </main>
</template>

<script>
import axios from 'axios';
import Autocomplete from '@/components/Autocomplete.vue';

export default {
  name: 'BbnVotes',
  components: { Autocomplete },
  props: {
    portfolioId: {
      type: Number,
      required: true,
    },
    edition: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      instruments: [],
      votes: [],
      tally: 10,
      payload: {
        analyst_id: 0,
        comment: '',
        edition: `${String(new Date().getMonth() + 1).padStart(2, 0)}-01-${new Date().getFullYear()}`,
        instrument_id: 0,
        points: 1,
        portfolio_id: 0,
        vote_id: null,
      },
    };
  },
  computed: {
    instrumentDict() {
      return new Map(this.instruments.map(instrument => {
        const label = `${instrument.exchange.symbol}:${instrument.symbol} ${instrument.company.name}`;
        return [label, instrument.id];
      }));
    },
    instrumentReverseDict() {
      return new Map(this.instruments.map(instrument => {
        const label = `${instrument.exchange.symbol}:${instrument.symbol} ${instrument.company.name}`;
        return [instrument.id, label];
      }));
    },
    instrumentLabels() {
      return this.instruments.map(instrument => `${instrument.exchange.symbol}:${instrument.symbol} ${instrument.company.name}`);
    },
    lastVote() {
      return this.votes.slice(4, 5).length ? this.votes.slice(4, 5)[0] : null;
    },
    bbnDict() {
      return new Map([[149, 2], [148, 10], [147, 11]]);
    },
  },
  watch: {
    portfolioId() {
      this.fetchMetadata();
    },
    edition() {
      this.fetchMetadata();
    },
  },
  methods: {
    initModal() {
      return this.materialize.Modal.init(document.getElementById('bbn-edit-modal'), {
        preventScrolling: false,
        dismissible: false,
      });
    },
    async fetchMetadata() {
      this.instruments = [];
      const promises = [
        axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/votes/possible_equities/?portfolio_id=${this.bbnDict.get(this.portfolioId)}`),
        axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/votes/?portfolio_id=${this.portfolioId}&edition=${this.edition}&analyst_id=${this.$auth.user.pk}`),
      ];
      const [instrumentsResp, votesResp] = await Promise.all(promises);
      this.instruments = instrumentsResp.data;
      this.votes = votesResp.data.votes;
      this.tally = votesResp.data.points_remaining;
      this.initModal();
    },
    handleInstrumentSelection(choice) {
      this.payload.instrument_id = this.instrumentDict.get(choice);
    },
    async saveVote() {
      this.payload.analyst_id = this.$auth.user.pk;
      this.payload.portfolio_id = this.portfolioId;
      this.payload.edition = this.edition;
      this.payload.comment = this.payload.comment.trim();
      if (![
        this.payload.analyst_id,
        this.payload.edition,
        this.payload.instrument_id,
        this.payload.points,
        this.payload.portfolio_id,
      ].every(arg => Boolean(arg))) {
        return this.toast('Please fill in all required fields', 'pink');
      }
      if (this.payload.points < 1 || this.payload.points > 6) {
        return this.toast('Points must be between 1-6', 'pink');
      }
      if (!this.payload.comment) {
        return this.toast('Please write a little comment why you chose this stock', 'orange');
      }
      const editionDate = new Date(this.payload.edition.replace('-', '/'));
      const todaysDate = new Date();

      // Get nextMonth's value (0-11). If current month is December, then reset to 0 for January.
      let nextMonth = todaysDate.getMonth() + 1;
      if (nextMonth === 12) {
        nextMonth = 0;
      }

      if (editionDate.getMonth() !== todaysDate.getMonth() && editionDate.getMonth() !== nextMonth) {
        return this.toast('Cannot submit vote for historic edition outside current month', 'pink');
      }
      const { data } = await axios.post(`${this.$store.getters.VUE_APP_APING_URL}/insights/votes/save/`, this.payload);
      this.toast('Saved!', 'green');
      this.payload.comment = '';
      this.payload.instrument_id = 0;
      this.payload.points = 1;
      this.payload.vote_id = null;
      const instrumentSearchBox = document.getElementById('bbn-instrument-search');
      if (instrumentSearchBox) {
        instrumentSearchBox.value = '';
      }
      const instrumentEditModalSearchBox = document.getElementById('bbn-edit-modal-instrument-search');
      if (instrumentEditModalSearchBox) {
        instrumentEditModalSearchBox.value = '';
      }
      this.fetchMetadata();
    },
    editModal(idx) {
      const vote = this.votes[idx];
      this.payload.instrument_id = vote.instrument.id;
      this.payload.points = vote.points;
      this.payload.comment = vote.comment;
      this.payload.vote_id = vote.id;
      document.getElementById('bbn-edit-modal-instrument-search').value = this.instrumentReverseDict.get(this.payload.instrument_id);
    },
  },
  mounted() {
    this.fetchMetadata();
  },
};
</script>

<style lang="scss" scoped>
@import '@/main.scss';

.inner-form {
  border: 1px solid $tmf-gains;
  border-radius: 10px;
}

.select-wrapper+label {
  top: 0px;
}
</style>
